import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import ProfileHeader from './components/ProfileHeader';
import LearningStats from './components/LearningStats';
import MissionHistory from './components/MissionHistory';
import AchievementShowcase from './components/AchievementShowcase';
import PerformanceAnalytics from './components/PerformanceAnalytics';
import BookmarkedMissions from './components/BookmarkedMissions';
import AccountSettings from './components/AccountSettings';

import Button from '../../components/ui/Button';

const UserProfileDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false); // Changed from true to false

  // Mock user data
  const mockUser = {
    id: 1,
    username: "selenium_master",
    email: "john.doe@example.com",
    bio: "Passionate about web automation and testing. Always learning new techniques to improve code quality and efficiency.",
    location: "San Francisco, CA",
    website: "https://johndoe.dev",
    joinedDate: "2024-03-15T00:00:00Z",
    level: 7,
    levelTitle: "Automation Expert",
    currentXP: 2850,
    nextLevelXP: 3500,
    twoFactorEnabled: true,
    settings: {
      notifications: {
        email: true,
        push: true,
        achievements: true,
        missionReminders: false,
        weeklyProgress: true
      },
      privacy: {
        profileVisibility: 'public',
        showProgress: true,
        showAchievements: true
      },
      preferences: {
        theme: 'dark',
        language: 'en',
        codeEditor: 'vs-dark',
        autoSave: true
      }
    }
  };

  const mockStats = {
    missionsCompleted: 47,
    totalMissions: 120,
    currentStreak: 12,
    bestStreak: 28,
    totalPracticeTime: 1847, // in minutes
    achievementsEarned: 23,
    totalAchievements: 45
  };

  const mockMissionHistory = [
    {
      id: 1,
      title: "Advanced Form Automation",
      difficulty: "hard",
      status: "completed",
      completedAt: "2025-01-04T16:30:00Z",
      executionTime: 1250,
      score: 95,
      xpEarned: 150
    },
    {
      id: 2,
      title: "Dynamic Content Scraping",
      difficulty: "medium",
      status: "completed",
      completedAt: "2025-01-04T14:15:00Z",
      executionTime: 890,
      score: 88,
      xpEarned: 100
    },
    {
      id: 3,
      title: "Multi-Tab Navigation",
      difficulty: "easy",
      status: "failed",
      completedAt: "2025-01-04T10:45:00Z",
      executionTime: 2100,
      score: 45,
      xpEarned: 0
    },
    {
      id: 4,
      title: "API Testing Integration",
      difficulty: "hard",
      status: "in-progress",
      completedAt: "2025-01-04T09:20:00Z",
      executionTime: 0,
      score: 0,
      xpEarned: 0
    },
    {
      id: 5,
      title: "Element Wait Strategies",
      difficulty: "medium",
      status: "completed",
      completedAt: "2025-01-03T18:30:00Z",
      executionTime: 750,
      score: 92,
      xpEarned: 120
    }
  ];

  const mockAchievements = [
    {
      id: 1,
      title: "First Steps",
      description: "Complete your first Selenium mission",
      type: "first-mission",
      category: "completion",
      rarity: "common",
      earned: true,
      earnedAt: "2024-03-16T00:00:00Z",
      xpReward: 50
    },
    {
      id: 2,
      title: "Speed Demon",
      description: "Complete a mission in under 500ms",
      type: "speed-demon",
      category: "performance",
      rarity: "rare",
      earned: true,
      earnedAt: "2024-05-22T00:00:00Z",
      xpReward: 100
    },
    {
      id: 3,
      title: "Perfectionist",
      description: "Achieve 100% accuracy on 5 consecutive missions",
      type: "perfectionist",
      category: "performance",
      rarity: "epic",
      earned: false,
      progress: { current: 3, target: 5 },
      xpReward: 200
    },
    {
      id: 4,
      title: "Streak Master",
      description: "Maintain a 30-day learning streak",
      type: "streak-master",
      category: "streak",
      rarity: "legendary",
      earned: false,
      progress: { current: 12, target: 30 },
      xpReward: 500
    },
    {
      id: 5,
      title: "Code Warrior",
      description: "Complete 50 missions successfully",
      type: "code-warrior",
      category: "completion",
      rarity: "epic",
      earned: false,
      progress: { current: 47, target: 50 },
      xpReward: 300
    }
  ];

  const mockAnalyticsData = {
    executionTrends: [
      { date: "Dec 28", executionTime: 1200 },
      { date: "Dec 29", executionTime: 980 },
      { date: "Dec 30", executionTime: 850 },
      { date: "Dec 31", executionTime: 920 },
      { date: "Jan 1", executionTime: 750 },
      { date: "Jan 2", executionTime: 680 },
      { date: "Jan 3", executionTime: 720 },
      { date: "Jan 4", executionTime: 650 }
    ],
    accuracyTrends: [
      { category: "Form Handling", accuracy: 92 },
      { category: "Navigation", accuracy: 88 },
      { category: "Data Extraction", accuracy: 95 },
      { category: "Element Interaction", accuracy: 85 },
      { category: "Wait Strategies", accuracy: 90 }
    ],
    codeQualityMetrics: [
      { label: "Clean Code", value: "94%", icon: "CheckCircle", color: "var(--color-success)" },
      { label: "Performance", value: "87%", icon: "Zap", color: "var(--color-warning)" },
      { label: "Best Practices", value: "91%", icon: "Star", color: "var(--color-primary)" },
      { label: "Error Handling", value: "89%", icon: "Shield", color: "var(--color-secondary)" }
    ],
    skillDistribution: [
      { name: "Web Scraping", value: 35 },
      { name: "Form Automation", value: 25 },
      { name: "Testing", value: 20 },
      { name: "Navigation", value: 15 },
      { name: "Performance", value: 5 }
    ],
    skillProgress: [
      { name: "Selenium WebDriver", level: 4, description: "Advanced automation techniques" },
      { name: "Python Scripting", level: 5, description: "Expert-level Python programming" },
      { name: "Web Technologies", level: 3, description: "HTML, CSS, JavaScript fundamentals" },
      { name: "Testing Frameworks", level: 3, description: "pytest, unittest, and more" },
      { name: "CI/CD Integration", level: 2, description: "Automation in deployment pipelines" }
    ],
    bestExecutionTime: 420,
    avgExecutionTime: 785,
    improvementPercentage: 23
  };

  const mockBookmarkedMissions = [
    {
      id: 10,
      title: "Advanced XPath Selectors",
      description: "Master complex XPath expressions for precise element targeting in dynamic web applications.",
      category: "web-scraping",
      difficulty: "hard",
      estimatedTime: 45,
      xpReward: 200,
      bookmarkedAt: "2025-01-02T00:00:00Z",
      personalNote: "Need to review the dynamic content handling section. The wait strategies here are crucial for my current project."
    },
    {
      id: 11,
      title: "Headless Browser Testing",
      description: "Learn to run Selenium tests in headless mode for faster execution and CI/CD integration.",
      category: "testing",
      difficulty: "medium",
      estimatedTime: 30,
      xpReward: 120,
      bookmarkedAt: "2025-01-01T00:00:00Z",
      personalNote: null
    },
    {
      id: 12,
      title: "Cross-Browser Compatibility",
      description: "Ensure your automation scripts work seamlessly across different browsers and versions.",
      category: "testing",
      difficulty: "hard",
      estimatedTime: 60,
      xpReward: 180,
      bookmarkedAt: "2024-12-30T00:00:00Z",
      personalNote: "Important for the upcoming project that needs to support Safari, Chrome, and Firefox."
    }
  ];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'achievements', label: 'Achievements', icon: 'Award' },
    { id: 'analytics', label: 'Analytics', icon: 'BarChart3' },
    { id: 'bookmarks', label: 'Bookmarks', icon: 'Bookmark' },
    { id: 'settings', label: 'Settings', icon: 'Settings' }
  ];

  useEffect(() => {
    // Immediately set user data without loading state to prevent homepage flash
    setUser(mockUser);
  }, []);

  const handleUpdateProfile = (updatedData) => {
    setUser(prev => ({ ...prev, ...updatedData }));
    console.log('Profile updated:', updatedData);
  };

  const handleUpdateSettings = (newSettings) => {
    setUser(prev => ({ ...prev, settings: newSettings }));
    console.log('Settings updated:', newSettings);
  };

  const handleRemoveBookmark = (missionId) => {
    console.log('Remove bookmark:', missionId);
  };

  const handleAddNote = (missionId, note) => {
    console.log('Add note to mission:', missionId, note);
  };

  const handleLogout = () => {
    // Clear all session data
    localStorage.removeItem('authToken');
    sessionStorage.removeItem('authToken');
    localStorage.removeItem('user');
    sessionStorage.removeItem('user');
    
    // Clear user state immediately
    setUser(null);
    
    // Navigate to home page (not login page) for better UX
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header user={user || mockUser} onLogout={handleLogout} />
      <main className="content-offset">
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Profile Header */}
          <div className="mb-8">
            <ProfileHeader user={user || mockUser} onUpdateProfile={handleUpdateProfile} />
          </div>

          {/* Tab Navigation */}
          <div className="flex flex-wrap gap-2 mb-8 border-b border-subtle">
            {tabs?.map((tab) => (
              <Button
                key={tab?.id}
                variant={activeTab === tab?.id ? "default" : "ghost"}
                size="sm"
                iconName={tab?.icon}
                iconPosition="left"
                onClick={() => setActiveTab(tab?.id)}
                className="mb-2"
              >
                {tab?.label}
              </Button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="space-y-8">
            {activeTab === 'overview' && (
              <>
                <LearningStats stats={mockStats} />
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                  <MissionHistory missions={mockMissionHistory} />
                  <div className="space-y-8">
                    <div className="bg-card rounded-lg border border-subtle p-6">
                      <h2 className="text-xl font-semibold text-foreground mb-4">Quick Actions</h2>
                      <div className="grid grid-cols-2 gap-4">
                        <Button
                          variant="outline"
                          iconName="Map"
                          iconPosition="left"
                          onClick={() => navigate('/mission-map-dashboard')}
                          className="h-16 flex-col"
                        >
                          <span className="text-sm">Explore Missions</span>
                        </Button>
                        <Button
                          variant="outline"
                          iconName="Code"
                          iconPosition="left"
                          onClick={() => navigate('/mission-workspace')}
                          className="h-16 flex-col"
                        >
                          <span className="text-sm">Practice Code</span>
                        </Button>
                        <Button
                          variant="outline"
                          iconName="BarChart3"
                          iconPosition="left"
                          onClick={() => navigate('/mission-results-summary')}
                          className="h-16 flex-col"
                        >
                          <span className="text-sm">View Results</span>
                        </Button>
                        <Button
                          variant="outline"
                          iconName="Users"
                          iconPosition="left"
                          className="h-16 flex-col"
                        >
                          <span className="text-sm">Community</span>
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {activeTab === 'achievements' && (
              <AchievementShowcase achievements={mockAchievements} />
            )}

            {activeTab === 'analytics' && (
              <PerformanceAnalytics analyticsData={mockAnalyticsData} />
            )}

            {activeTab === 'bookmarks' && (
              <BookmarkedMissions
                bookmarkedMissions={mockBookmarkedMissions}
                onRemoveBookmark={handleRemoveBookmark}
                onAddNote={handleAddNote}
              />
            )}

            {activeTab === 'settings' && (
              <AccountSettings user={user || mockUser} onUpdateSettings={handleUpdateSettings} />
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default UserProfileDashboard;